import Routes from "./Routes";
import { toast, ToastContainer } from "react-toastify";
import { BrowserRouter, useLocation } from "react-router";
import { ProvidePeptidePricing } from "./hooks/usePeptidePricing.js";
import { ProvidePeptideCart } from "./hooks/usePeptideCart.js";
import { useAuth } from "./hooks/useAuth";
import { useFeatures, getUsedFeaturesFromChats } from "./hooks/useFeatures";
import { useChats } from "./hooks/useChats";
import { useTheme } from "./hooks/useTheme";
import RecorderBar from "./components/layout/RecorderBar";
import OfflineModeBar from "./components/layout/OfflineModeBar";
import RecordingRecoveryBar from "./components/layout/RecordingRecoveryBar";
import ImpersonationBanner from "./components/layout/ImpersonationBanner";
import SubscriptionLapsedBanner from "./components/layout/SubscriptionLapsedBanner";
import ModalLocationSelect from "./components/layout/ModalLocationSelect";
import ModalUpgradeRequired from "./components/common/ModalUpgradeRequired";
import ModalTierRecommendation from "./components/common/ModalTierRecommendation";
import { useEHR } from "./hooks/useEHR";
import { usePostMessages } from "./hooks/usePostMessages";
import ModalSelectTabEhr from "./components/ehr/ModalSelectTabEhr";
import { useMemo, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import Header from "./components/layout/Header";
import classNames from "classnames";
import RightSidebar from "./components/layout/RightSidebar";
import RecordingsModal from "./components/layout/RecordingsModal";
import RecordingsBacklogAlert from "./components/layout/RecordingsBacklogAlert";
import { useRightSidebarDrawer } from "./hooks/useRightSidebarDrawer";
import ModalDMESetupReminder from "./components/common/ModalDMESetupReminder";
import ModalRequiredDocsReminder from "./components/common/ModalRequiredDocsReminder";
import ModalPsaReminder from "./components/common/ModalPsaReminder";
import ModalReceivedContractsReminder from "./components/common/ModalReceivedContractsReminder";
import RequiredDocsLockHost from "./components/common/RequiredDocsLockHost";
import { getRequiredDocsLock } from "./utils/requiredDocsLock";
import {
  hasUnsignedReceivedContracts,
  hasReceivedPsaContract,
  unsignedReceivedContracts,
  canSignOrgContracts,
} from "./utils/receivedContracts";
import ModalHealthCheck from "./components/common/ModalHealthCheck";
import ModalLostRecording from "./components/common/ModalLostRecording";
import ModalWebRecordingRecovery from "./components/common/ModalWebRecordingRecovery";
import { useRecorder } from "./hooks/useRecorder";
import { ProvideDemo } from "./hooks/useDemo";
import DemoBanner from "./components/demo/DemoBanner";
import DemoRoleSwitcher from "./components/demo/DemoRoleSwitcher";
import DevPreviewBadge from "./components/common/DevPreviewBadge";
import rpgFavicon from "./images/rpg_square.svg";
import healcoreFavicon from "./images/healcore_square.png";
import freehealthFavicon from "./images/freehealth_square.svg";
import { initActivityLog } from "./utils/activityLog";
import { getToastContainerConfig } from "./utils/toastContainerConfig";
import { attachToastRailLifecycle } from "./utils/toastRailController";
import { shouldUseNativeMobileRecordingUi } from "./utils/mobileRecordingUi";
import DmeOrderNotificationBridge from "./components/orders/DmeOrderNotificationBridge";

const FAVICON_BY_BRAND = {
  rpg: rpgFavicon,
  healcore: healcoreFavicon,
  freehealthai: freehealthFavicon,
};

// Brands whose tab title replaces the static "LucasAI - Doctor" default.
const TITLE_BY_BRAND = {
  freehealthai: "FreeHealth.ai",
};

// version for mobile app download alert - bump to show alert again
const MOBILE_APP_ALERT_VERSION = 1;

// minimum required app versions (fallback defaults)
const MIN_APP_VERSION_IOS = "2.3.5";
const MIN_APP_VERSION_ANDROID = "2.3.5";
const APP_MANDATORY_UPDATE = true;

const IOS_STORE_URL = "https://apps.apple.com/app/apple-store/id6741919793?pt=127626793";
const ANDROID_STORE_URL = "https://play.google.com/store/apps/details?id=com.lucasaiorg.lucasaiapp&utm_source=website";

// The ChiroApp shell supplies its own header/sidebars, but only on the routes
// it actually mounts on (home "/" and "/chat/:id"). On those routes we hide the
// standard Header/RightSidebar so the chrome never double-renders behind the
// shell. On every OTHER route (settings, profile, user management, etc.) the
// chiro shell is NOT mounted — so we must keep the standard chrome, otherwise a
// chiropractor has no top navigation at all and gets stranded with no way home.
function ChromeGate({ isChiro, children }) {
  const { pathname } = useLocation();
  const onChiroShellRoute = pathname === "/" || pathname.startsWith("/chat/");
  if (isChiro && onChiroShellRoute) return null;
  return children;
}

function RecorderBarGate({ isMobile, isMobileApp, isChromeExtension, isChiro }) {
  const { pathname } = useLocation();
  const chatDetailOwnsMobileControls = shouldUseNativeMobileRecordingUi({
    isMobile,
    isMobileApp,
    isChromeExtension,
    isChiro,
    pathname,
  });

  if (chatDetailOwnsMobileControls) return null;
  return <RecorderBar />;
}

function Layout() {
  const { t } = useTranslation();
  const { isMobile, isMobileApp, isServerAvailable, brand, auth, isChromeExtension, setShowLeftSidebar, hasGroup, isAdminUser } =
    useAuth();
  // Chiropractors run a full-bleed, self-contained shell (ChiroApp) that
  // supplies its own header, sidebars and action panel. Suppress the standard
  // app chrome for them so it never double-renders behind the chiro shell.
  const isChiro = auth?.user?.custom?.doctorType === "chiropractor";
  const { showModalSelectTab } = useEHR();
  const { requestedFeature, closeUpgradeModal, showTierRecommendation, closeTierRecommendation, hasFeature } = useFeatures();
  const { chats } = useChats();
  const { sendAppUpdateInfo } = usePostMessages();
  const appUpdateMessageSentRef = useRef(false);
  const usedFeatures = useMemo(() => getUsedFeaturesFromChats(chats), [chats]);
  const { isDark, initializeFromProfile } = useTheme();
  const { showModalHealthCheck, closeHealthCheckAndContinue } = useRecorder();
  const { showRightSidebar, rightSidebarClosing, closeRightSidebar, toggleRightSidebar } = useRightSidebarDrawer();
  const [showDMEReminder, setShowDMEReminder] = useState(false);
  const [showRequiredDocsReminder, setShowRequiredDocsReminder] = useState(false);
  const [showPsaReminder, setShowPsaReminder] = useState(false);
  const [showReceivedContractsReminder, setShowReceivedContractsReminder] = useState(false);
  // Deadline-bearing provider orgs get the one required-documents prompt
  // (RequiredDocsLockHost) instead of the three legacy reminders, so nobody is
  // nagged twice about the same documents. Grandfathered orgs carry no stamps →
  // hasDeadline is false → every legacy reminder behaves exactly as before.
  const docsLock = useMemo(() => getRequiredDocsLock(auth, { isAdminUser }), [auth, isAdminUser]);
  const docsLockOwnsReminders = docsLock.applies && docsLock.hasDeadline;

  useEffect(() => {
    initActivityLog();
  }, []);

  // Native app only: one central toast.onChange interception point owns the
  // quiet rail's counts, per-severity lifetimes, and content adapter. Off the
  // native app this attaches nothing at all, so browser toasts stay untouched.
  useEffect(() => {
    const rail = attachToastRailLifecycle({ isMobileApp, toastApi: toast });
    if (!rail) return undefined;
    return () => rail.detach();
  }, [isMobileApp]);

  useEffect(() => {
    const href = FAVICON_BY_BRAND[brand] || "/logo.png";
    const icon = document.querySelector("link[rel='icon']");
    const appleIcon = document.querySelector("link[rel='apple-touch-icon']");
    if (icon) icon.href = href;
    if (appleIcon) appleIcon.href = href;
    if (TITLE_BY_BRAND[brand]) document.title = TITLE_BY_BRAND[brand];
  }, [brand]);

  useEffect(() => {
    if (!auth?.user || !isMobile || isMobileApp || isChromeExtension) {
      return;
    }

    const shownVersion = sessionStorage.getItem("mobileAppAlertVersion");
    if (shownVersion && parseInt(shownVersion) >= MOBILE_APP_ALERT_VERSION) {
      return;
    }

    const timer = setTimeout(() => {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const message = isIOS ? t("layout.mobileAppAlert.ios") : t("layout.mobileAppAlert.android");

      const confirmed = window.confirm(message);
      sessionStorage.setItem("mobileAppAlertVersion", MOBILE_APP_ALERT_VERSION.toString());

      if (confirmed) {
        const storeUrl = isIOS ? `${IOS_STORE_URL}&ct=website&mt=8` : `${ANDROID_STORE_URL}&utm_medium=web&utm_campaign=website`;

        // NOTE: using location.href to ensure it works, browsers may block window.open in some cases
        window.location.href = storeUrl;
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [auth?.user, isChromeExtension, isMobile, isMobileApp, t]);

  useEffect(() => {
    if (auth?.user?.custom?.themePreference) {
      initializeFromProfile(auth.user.custom.themePreference);
    }
    // eslint-disable-next-line
  }, [auth?.user?.custom?.themePreference]);

  useEffect(() => {
    if (!isMobileApp || !auth?.user || appUpdateMessageSentRef.current) {
      return;
    }

    const updateInfo = {
      minVersionIOS: auth?.brain?.variables?.minAppVersionIOS ?? MIN_APP_VERSION_IOS,
      minVersionAndroid: auth?.brain?.variables?.minAppVersionAndroid ?? MIN_APP_VERSION_ANDROID,
      isMandatory: auth?.brain?.variables?.isAppUpdateMandatory ?? APP_MANDATORY_UPDATE,
      title: t("layout.appUpdate.title"),
      titleOptional: t("layout.appUpdate.titleOptional"),
      message: t("layout.appUpdate.message", { current: "{{current}}", required: "{{required}}" }),
      updateButton: t("layout.appUpdate.updateButton"),
      laterButton: t("layout.appUpdate.laterButton"),
      iosStoreUrl: `${IOS_STORE_URL}&ct=update&mt=8`,
      androidStoreUrl: `${ANDROID_STORE_URL}&utm_medium=app&utm_campaign=update`,
    };

    appUpdateMessageSentRef.current = true;
    sendAppUpdateInfo(updateInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.user, auth?.brain?.variables, isMobileApp, t]);

  // DME Setup Reminder - show for provider supervisors (not manufacturer) who haven't completed DME setup
  useEffect(() => {
    if (!auth?.user) return;

    // Check if already dismissed this session
    const dismissed = sessionStorage.getItem("dmeReminderDismissed");
    if (dismissed) return;

    // Check conditions:
    // 1. User is supervisor
    // 2. Provider org (has "orders" but not "ordersManagement")
    // 3. Onboarding wizard completed (doctorConsentAuthorizeSignatureDate exists)
    // 4. DME setup NOT complete (dmeList empty — covers both own DME and manufacturer DME)
    // 5. DME product line is enabled (peptides-only orgs don't need DME billing setup)
    const isSupervisor = hasGroup("supervisor");
    const isProviderOrg = hasFeature("orders") && !hasFeature("ordersManagement");
    const onboardingComplete = !!auth.user?.custom?.doctorConsentAuthorizeSignatureDate;
    const dmeSetupComplete = auth.orgInfo?.dmeList && auth.orgInfo.dmeList.length > 0;
    const dmeEnabled = auth.orgInfo?.productLines?.dme === true;

    if (isSupervisor && isProviderOrg && onboardingComplete && !dmeSetupComplete && dmeEnabled) {
      // Delay showing the reminder to not overwhelm on login
      const timer = setTimeout(() => {
        setShowDMEReminder(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [auth?.user, auth?.orgInfo, hasGroup, hasFeature]);

  const closeDMEReminder = () => {
    setShowDMEReminder(false);
    sessionStorage.setItem("dmeReminderDismissed", "true");
  };

  // Required Documents Reminder - show after DME reminder is dismissed (or if it doesn't apply)
  useEffect(() => {
    if (!auth?.user) return;
    if (docsLockOwnsReminders) return; // the required-documents prompt covers this org
    if (showDMEReminder) return; // wait for DME reminder to be dismissed first
    const dismissed = sessionStorage.getItem("requiredDocsReminderDismissed");
    if (dismissed) return;

    const isSupervisor = hasGroup("supervisor");
    const isOrderProvider = auth?.session?.currentOrganizationPlan === "order-provider";
    const onboardingComplete = !!auth.user?.custom?.doctorConsentAuthorizeSignatureDate;
    const docsCompleted = !!auth.orgInfo?.newAccountForm?.completedAt;
    // The required-docs flow is DME credentialing paperwork (NAF, etc.).
    // Peptides-only orgs don't need it.
    const dmeEnabled = auth.orgInfo?.productLines?.dme === true;

    if (isSupervisor && isOrderProvider && onboardingComplete && !docsCompleted && dmeEnabled) {
      const timer = setTimeout(() => {
        setShowRequiredDocsReminder(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [auth?.user, auth?.orgInfo, auth?.session?.currentOrganizationPlan, hasGroup, showDMEReminder, docsLockOwnsReminders]);

  const closeRequiredDocsReminder = () => {
    setShowRequiredDocsReminder(false);
    sessionStorage.setItem("requiredDocsReminderDismissed", "true");
  };

  // Product Service Agreement reminder — only fires when the manufacturer
  // toggled "Require in-app PSA" on the onboarding link AND the provider
  // has DME activated AND the PSA hasn't been signed yet. Queued AFTER the
  // DME and Required-Docs reminders so it never stacks on top of them.
  useEffect(() => {
    if (!auth?.user) return;
    if (docsLockOwnsReminders) return; // the required-documents prompt covers this org
    if (showDMEReminder || showRequiredDocsReminder) return;
    if (sessionStorage.getItem("psaReminderDismissed")) return;

    const requirePsa = auth.orgInfo?.requirePsa === true;
    const dmeEnabled = auth.orgInfo?.productLines?.dme === true;
    const onboardingComplete = !!auth.user?.custom?.doctorConsentAuthorizeSignatureDate;
    const psaSigned = !!auth.orgInfo?.productServiceAgreement?.signedAt;
    // Signer gate shared with the received-contracts reminder (provider account
    // holder or supervisor; never a sales rep) — see canSignOrgContracts.
    const canSignPsa = canSignOrgContracts(auth, hasGroup);

    // Suppress once the manufacturer's PSA arrives as an auto-pushed contract:
    // the received-contracts reminder below owns that case and routes to the
    // real signing surface. This legacy reminder keeps firing only for orgs
    // that have requirePsa but no pushed PSA contract.
    if (requirePsa && dmeEnabled && onboardingComplete && !psaSigned && canSignPsa && !hasReceivedPsaContract(auth.orgInfo)) {
      const timer = setTimeout(() => setShowPsaReminder(true), 2500);
      return () => clearTimeout(timer);
    }
  }, [
    auth?.user,
    auth?.orgInfo,
    auth?.session?.currentOrganizationPlan,
    hasGroup,
    showDMEReminder,
    showRequiredDocsReminder,
    docsLockOwnsReminders,
  ]);

  const closePsaReminder = () => {
    setShowPsaReminder(false);
    sessionStorage.setItem("psaReminderDismissed", "true");
  };

  // Received-contracts reminder — the manufacturer auto-pushes the pinned PSA
  // (and any other contracts) onto orgInfo.customContracts at signup. Surface
  // unsigned ones on login so a new provider is prompted to sign instead of
  // hunting through Settings → Required Documents → Received Contracts. Queued
  // AFTER the DME / Required-Docs / PSA reminders so it never stacks on them.
  useEffect(() => {
    if (!auth?.user) return;
    if (docsLockOwnsReminders) return; // the required-documents prompt covers this org
    if (showDMEReminder || showRequiredDocsReminder || showPsaReminder) return;
    if (sessionStorage.getItem("receivedContractsReminderDismissed")) return;

    // Signer gate shared with the PSA reminder. Also require onboarding to be
    // complete so we don't prompt before the provider has finished account
    // setup — matching the DME / Required-Docs / PSA reminders.
    const canSign = canSignOrgContracts(auth, hasGroup);
    const onboardingComplete = !!auth.user?.custom?.doctorConsentAuthorizeSignatureDate;

    if (canSign && onboardingComplete && hasUnsignedReceivedContracts(auth.orgInfo)) {
      const timer = setTimeout(() => setShowReceivedContractsReminder(true), 2500);
      return () => clearTimeout(timer);
    }
  }, [
    auth?.user,
    auth?.orgInfo,
    auth?.session?.currentOrganizationPlan,
    hasGroup,
    showDMEReminder,
    showRequiredDocsReminder,
    showPsaReminder,
    docsLockOwnsReminders,
  ]);

  const closeReceivedContractsReminder = () => {
    setShowReceivedContractsReminder(false);
    sessionStorage.setItem("receivedContractsReminderDismissed", "true");
  };

  return (
    <BrowserRouter>
      <ProvidePeptidePricing>
        <ProvidePeptideCart>
          <ProvideDemo>
            <DmeOrderNotificationBridge />
            {/* Inside BrowserRouter: the prompt navigates to the signing
                surfaces. Self-gating — renders nothing for exempt or unstamped
                orgs; locked orgs get the locked prompt once per session and
                again whenever a sign or order is refused. */}
            <RequiredDocsLockHost lock={docsLock} deferred={showDMEReminder} />
            <div
              className={
                "main-wrapper " +
                ("brand-" + brand) +
                (isMobile ? " isMobile" : " isDesktop") +
                // The `.isOffline` rules in _responsive.scss reserve
                // $offlinebar_height for OfflineModeBar, which renders whenever
                // !(isOnline && serverReachable) -- i.e. !isServerAvailable. Gating
                // this on isOnline alone left the bar overlapping the header
                // during a server outage.
                (isServerAvailable ? " isOnline" : " isOffline") +
                (window.ReactNativeWebView !== undefined ? " isMobileApp" : "")
              }
              id="main-wrapper"
            >
              <OfflineModeBar></OfflineModeBar>
              <RecordingRecoveryBar />
              <ImpersonationBanner />
              <SubscriptionLapsedBanner />
              <DemoBanner />
              {/* Fixed-position banner — self-gates: renders null in prod
                  and when no ?devPreview=<name> is active. Mounted at app
                  root so any preview surfaces it once, regardless of which
                  drawer/modal the tester opens. */}
              <DevPreviewBadge />

              <div className="main-content">
                {auth?.user && (
                  <ChromeGate isChiro={isChiro}>
                    <Header
                      setShowLeftSidebar={setShowLeftSidebar}
                      setShowRightSidebar={toggleRightSidebar}
                      showRightSidebar={showRightSidebar}
                    ></Header>
                  </ChromeGate>
                )}

                <div className="body-wrapper">
                  <Routes />
                </div>

                {auth?.user && (
                  <ChromeGate isChiro={isChiro}>
                    <div
                      className={classNames(
                        "sidebar-wrapper right",
                        // Stay displayed through the close animation, then unmount.
                        (showRightSidebar || rightSidebarClosing) && "show",
                        rightSidebarClosing && !showRightSidebar && "closing"
                      )}
                      onClick={closeRightSidebar}
                    >
                      {
                        <div className="sidebar full-size" onClick={(e) => e.stopPropagation()}>
                          <RightSidebar setShowRightSidebar={closeRightSidebar}></RightSidebar>
                        </div>
                      }
                    </div>
                  </ChromeGate>
                )}
              </div>

              {auth?.user && <ModalLocationSelect />}

              {showModalSelectTab && <ModalSelectTabEhr />}

              {showTierRecommendation && <ModalTierRecommendation usedFeatures={usedFeatures} close={closeTierRecommendation} />}

              {requestedFeature && !showTierRecommendation && (
                <ModalUpgradeRequired featureName={requestedFeature} close={closeUpgradeModal} />
              )}

              {showDMEReminder && <ModalDMESetupReminder close={closeDMEReminder} />}

              {showRequiredDocsReminder && !docsLockOwnsReminders && (
                <ModalRequiredDocsReminder close={closeRequiredDocsReminder} />
              )}

              {showPsaReminder && !docsLockOwnsReminders && <ModalPsaReminder close={closePsaReminder} />}

              {/* Render-time mutual exclusion: never stack on another reminder
                  (the show-flags flip via timers, so the effect guard alone
                  can't prevent a simultaneous fire). Also re-check the live
                  count so a contract signed between trigger and render can't
                  show a stale "you have a contract" prompt. */}
              {showReceivedContractsReminder &&
                !docsLockOwnsReminders &&
                !showPsaReminder &&
                !showDMEReminder &&
                !showRequiredDocsReminder &&
                unsignedReceivedContracts(auth?.orgInfo).length > 0 && (
                  <ModalReceivedContractsReminder
                    close={closeReceivedContractsReminder}
                    count={unsignedReceivedContracts(auth?.orgInfo).length}
                  />
                )}

              <ModalHealthCheck show={showModalHealthCheck} close={closeHealthCheckAndContinue} brand={brand} />
              <ModalLostRecording />
              <ModalWebRecordingRecovery />

              {auth?.user && <RecordingsModal />}
              {/* Not in the native shell: it has its own Local Recordings screen
                  over its own store, and the web modal's two answers to this alert
                  are hollow there — Save is hidden (an anchor download does nothing
                  in the WebView) and Retry is a no-op for the rows it would list. */}
              {auth?.user && !isMobileApp && <RecordingsBacklogAlert />}

              <ToastContainer {...getToastContainerConfig({ isMobileApp, isMobile, isDark })} />
              <RecorderBarGate
                isMobile={isMobile}
                isMobileApp={isMobileApp}
                isChromeExtension={isChromeExtension}
                isChiro={isChiro}
              />
              <DemoRoleSwitcher />
            </div>
          </ProvideDemo>
        </ProvidePeptideCart>
      </ProvidePeptidePricing>
    </BrowserRouter>
  );
}

export default Layout;
